import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { AlertCircle } from "lucide-react";
import { logger } from "@/infrastructure/lib/logger";

export default function OAuthError() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isRetrying, setIsRetrying] = useState(false);

  // Obter mensagem de erro e invite_token da query string
  const errorMessage = searchParams.get("message") || "";
  const inviteToken = searchParams.get("invite_token");
  
  // Determinar mensagem amigável baseada no erro
  const getFriendlyMessage = (): string => {
    const lowerMessage = errorMessage.toLowerCase();
    
    if (lowerMessage.includes("access_denied") || lowerMessage.includes("cancelled")) {
      return t("auth.oauth.error.accessDenied");
    }
    
    if (lowerMessage.includes("user cancelled")) {
      return t("auth.oauth.error.userCancelled");
    }
    
    if (lowerMessage.includes("authentication failed")) {
      return t("auth.oauth.error.authFailed");
    }

    if (lowerMessage.includes("email") && (lowerMessage.includes("corresponde") || lowerMessage.includes("mismatch") || lowerMessage.includes("match"))) {
      return t("auth.oauth.error.emailMismatch");
    }

    if (lowerMessage.includes("expirado") || lowerMessage.includes("expired")) {
      return t("auth.oauth.error.inviteExpired");
    }

    if (lowerMessage.includes("inválido") || lowerMessage.includes("invalid") || lowerMessage.includes("utilizado") || lowerMessage.includes("used")) {
      return t("auth.oauth.error.inviteInvalid");
    }

    if (lowerMessage === "pending_invite") {
      return t("auth.oauth.error.pendingInvite");
    }

    return t("auth.oauth.error.genericError");
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  const handleTryAgain = async () => {
    setIsRetrying(true);
    try {
      if (inviteToken) {
        navigate(`/invite/password?invite_token=${encodeURIComponent(inviteToken)}`);
      } else {
        navigate("/login");
      }
    } catch (error) {
      logger.error('[OAuthError] Error retrying', { error: error instanceof Error ? error.message : String(error) });
      navigate("/login");
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative">
      <div className="w-full max-w-md space-y-4 animate-fade-in">
        <div className="flex justify-center">
          <img 
            src="/assets/logo.png" 
            alt="Prodgy Logo" 
            className="h-32 w-auto mb-2"
          />
        </div>
        
        <Card className="w-full shadow-lg border-destructive/50">
          <CardHeader className="space-y-3 pb-4">
            <div className="mx-auto mb-2">
              <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-destructive" />
              </div>
            </div>
            <CardTitle className="text-2xl font-medium tracking-tight text-center">
              {t("auth.oauth.error.title")}
            </CardTitle>
            <CardDescription className="text-center">
              {getFriendlyMessage()}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Mostrar mensagem técnica se houver (para debugging) */}
            {errorMessage && process.env.NODE_ENV === 'development' && (
              <div className="p-3 bg-muted rounded-md">
                <p className="text-xs text-muted-foreground font-mono break-words">
                  {errorMessage}
                </p>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col gap-3">
            <Button 
              onClick={handleTryAgain}
              className="w-full font-normal h-11"
              disabled={isRetrying}
            >
              {isRetrying ? t("auth.oauth.connecting") : t("auth.oauth.error.tryAgain")}
            </Button>
            <Button 
              variant="outline"
              onClick={handleBackToLogin}
              className="w-full font-normal h-11"
              disabled={isRetrying}
            >
              {t("auth.oauth.error.backToLogin")}
            </Button>
          </CardFooter>
        </Card>
      </div>
      
      {/* Powered by footer */}
      <div className="absolute bottom-4 right-4">
        <span className="text-sm text-muted-foreground opacity-80">
          {t("common.poweredBy")}{' '}
          <a 
            href="https://programmers.com.br" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:text-muted-foreground/80 transition-colors"
          >
            {t("common.companyName")}
          </a>
          {t("common.trademark")}
        </span>
      </div>
    </div>
  );
}
